import type { DefaultError, FetchQueryOptions, QueryClient, QueryKey } from '@tanstack/react-query';

import { client } from '@connected/api-client';
import { useMediaManager } from '@connected/api-client/hooks/use-media-manager';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { DEFAULT_IMAGE_QUALITY } from '@connected/ui/config/file';
import { useCallback } from 'react';

import { logger } from '@/lib/logger';
import { blobToFile } from '@/lib/utils';

import type { ElectricalProjectFile } from './use-electrical-project-files';

import { useElectricalProjectFiles } from './use-electrical-project-files';

export function usePrefetch({ queryClient, electricalProjectVersionId }: { queryClient: QueryClient; electricalProjectVersionId: string }) {
    function ensureQuery<TQueryFnData = unknown, TError = DefaultError, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(queryOptions: FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey>, prefetch?: true): Promise<void>;
    function ensureQuery<TQueryFnData = unknown, TError = DefaultError, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(queryOptions: FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey>, prefetch?: false): Promise<TQueryFnData>;
    function ensureQuery<TQueryFnData = unknown, TError = DefaultError, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(queryOptions: FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey>, prefetch: boolean = true): Promise<void | TQueryFnData> {
        // const queryKey = queryOptions.queryKey;

        // if (!queryKey) {
        //     throw new Error('Query key is required');
        // }

        // const existingData = queryClient.getQueryData(queryKey);

        // if (existingData) {
        //     return Promise.resolve(existingData) as Promise<TQueryFnData | void>;
        // }

        if (prefetch) {
            return queryClient.prefetchQuery(queryOptions) as Promise<void>;
        }

        return queryClient.fetchQuery(queryOptions) as unknown as Promise<TQueryFnData>;
    };
    const mediaManager = useMediaManager();
    const { generateMediaFileId } = useElectricalProjectFiles();

    const prefetchData = useCallback(async () => {
        const promises = [
            // File upload endpoints
            ensureQuery(elektraAppQueryOptions.files.distributionBoardImage.upload()),
            ensureQuery(elektraAppQueryOptions.files.finalCircuitImage.upload()),
            ensureQuery(elektraAppQueryOptions.files.observationImage.upload()),
            ensureQuery(elektraAppQueryOptions.files.projectImage.upload()),
            ensureQuery(elektraAppQueryOptions.files.technicalDrawingImage.upload()),
            ensureQuery(elektraAppQueryOptions.files.floorPlanImage.upload()),
            ensureQuery(elektraAppQueryOptions.files.pvInverterImage.upload()),
            ensureQuery(elektraAppQueryOptions.files.pvInstallation.upload()),
        ];

        await Promise.all(promises);

        // Prefetch all queries related to the applications

        ensureQuery(elektraAppQueryOptions.project.request.inspectionReportsListOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.distributionBoardDeviceListOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.risoMeasurementOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.inspection.controlPoints.ebMeasurementEditList({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.request.objectOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.request.organizationOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.request.technicalDrawingsQueryOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.quotation.quotationOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.inspectionPlan.inspectionPlanEditOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.inspectionPlan.inspectionNormsListOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({
            electricalProjectVersionId,
            listKind: 'AdditionalInstruction',
        }));

        ensureQuery(elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({
            electricalProjectVersionId,
            listKind: 'AdditionalTopic',
        }));

        ensureQuery(elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({
            electricalProjectVersionId,
            listKind: 'BuildingFunction',
        }));

        ensureQuery(elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({
            electricalProjectVersionId,
            listKind: 'InspectionComponent',
        }));

        ensureQuery(elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({
            electricalProjectVersionId,
            listKind: 'InspectionExclusion',
        }));

        ensureQuery(elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({
            electricalProjectVersionId,
            listKind: 'ObjectSpecialSpace',
        }));

        ensureQuery(elektraAppQueryOptions.project.inspection.information.inspectionsEditOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.inspection.information.selectableMeasurementDevicesListOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.inspection.information.selectedMeasurementDevicesListOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.inspection.conclusion.conclusionEditOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.recapOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.recapViewOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.schedule.scheduleOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.schedule.projectUsersListOptions({
            electricalProjectVersionId,
            userKind: 'Inspector',
        }));

        ensureQuery(elektraAppQueryOptions.project.schedule.projectUsersListOptions({
            electricalProjectVersionId,
            userKind: 'CertifiedInspector',
        }));

        ensureQuery(elektraAppQueryOptions.project.inspection.distributionBoard.options.distributionBoardValuesOptions());
        ensureQuery(elektraAppQueryOptions.project.inspection.distributionBoard.options.ingressProtectionRatingsOptions());
        ensureQuery(elektraAppQueryOptions.project.inspection.distributionBoard.options.residualCurrentDeviceOptionsListOptions());
        ensureQuery(elektraAppQueryOptions.project.inspection.controlPoints.ebMeasurementEditList({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.checklistTextGetOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.inspection.observations.classificationGetOptions({
            electricalProjectVersionId,
        }));

        ensureQuery(elektraAppQueryOptions.project.inspection.observations.standardTextsGetOptions({
            electricalProjectVersionId,
            objectKind: 'DistributionBoard',
        }));

        ensureQuery(elektraAppQueryOptions.project.inspection.observations.standardTextsGetOptions({
            electricalProjectVersionId,
            objectKind: 'DistributionBoardDevice',
        }));

        ensureQuery(elektraAppQueryOptions.project.inspection.observations.standardTextsGetOptions({
            electricalProjectVersionId,
            objectKind: 'DistributionBoardFile',
        }));

        ensureQuery(elektraAppQueryOptions.project.inspection.observations.standardTextsGetOptions({
            electricalProjectVersionId,
            objectKind: 'FinalCircuit',
        }));

        ensureQuery(elektraAppQueryOptions.project.inspection.observations.standardTextsGetOptions({
            electricalProjectVersionId,
            objectKind: 'PVInverter',
        }));

        ensureQuery(elektraAppQueryOptions.project.inspection.observations.standardTextsGetOptions({
            electricalProjectVersionId,
            objectKind: 'PVString',
        }));

        ensureQuery(elektraAppQueryOptions.project.inspection.observations.standardTextsGetOptions({
            electricalProjectVersionId,
            objectKind: 'RisoMeasurement',
        }));

        ensureQuery(elektraAppQueryOptions.project.observationsListOptions({
            electricalProjectVersionId,
        }));

        const distributionBoards = await ensureQuery(
            elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({
                electricalProjectVersionId,
            }),
            false,
        );

        const finalCircuits = await ensureQuery(elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitListOptions({
            electricalProjectVersionId,
        }), false);

        distributionBoards.DistributionBoards?.forEach(async (distributionBoard) => {
            await ensureQuery(
                elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({
                    electricalProjectDistributionBoardId: distributionBoard.ElectricalProjectDistributionBoardId!,
                }),
                false,
            );

            await ensureQuery(elektraAppQueryOptions.project.inspection.distributionBoard.checkListItemsOptions({
                electricalProjectDistributionBoardId: distributionBoard.ElectricalProjectDistributionBoardId!,
                electricalProjectVersionId,
            }), false);
        });

        finalCircuits.FinalCircuits?.forEach(async (finalCircuit) => {
            await ensureQuery(elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitOptions({
                electricalProjectFinalCircuitId: finalCircuit.ElectricalProjectFinalCircuitId!,
            }));
        });
    }, [electricalProjectVersionId, ensureQuery]);

    const prefetchFiles = useCallback(async () => {
        const photoList = await ensureQuery(elektraAppQueryOptions.project.files.listOptions({
            electricalProjectVersionId,
        }), false);

        logger.info(['Prefetched photo list with', photoList?.Files?.length, 'files']);

        if (photoList?.Files) {
            logger.info('Starting download of all files in background');
            Promise.all(
                photoList.Files.map(async (file) => {
                    try {
                        const mediafileId = generateMediaFileId(file);

                        if (await mediaManager.fileExists(mediafileId)) {
                            logger.info(['File already exists in media manager, skipping prefetch:', file.ElectricalProjectFileId]);
                            return;
                        }

                        logger.info(['Prefetching file', file.ElectricalProjectFileId]);
                        const response = await client.GET('/api/v2/electrical/projects/files/download', {
                            params: {
                                query: {
                                    ElectricalProjectFileId: file.ElectricalProjectFileId ?? '',
                                    FileId: file?.File?.FileId ?? null,
                                    ImageQuality: DEFAULT_IMAGE_QUALITY,
                                },
                            },
                            parseAs: 'blob',
                        });

                        if (response.error) {
                            logger.warn(['Failed to prefetch file:', file.ElectricalProjectFileId]);
                        }

                        const newFile: ElectricalProjectFile = {
                            ElectricalProjectVersionId: file.ElectricalProjectFileId,
                            ElectricalProjectFileId: file.ElectricalProjectFileId,
                            Description: file.Description,
                            Order: file.Order,
                            HasFile: true,
                            File: {
                                FileId: file.File?.FileId,
                                Name: file.File?.Name ?? '',
                                ContentType: file.File?.ContentType ?? '',
                            },
                            Kind: file.Kind,
                        };

                        const blobFile = blobToFile(response.data!, file.File?.Name ?? '', file.File?.ContentType ?? '');

                        logger.info(['Uploading prefetched file to media manager:', newFile.ElectricalProjectFileId, mediafileId]);

                        await mediaManager.upload(blobFile, mediafileId);
                    }
                    catch (error) {
                        logger.warn(['Error prefetching file:', error]);
                    }
                }),
            ).catch((error) => {
                logger.warn(['Some file prefetches failed:', error]);
            });
        }
    }, [electricalProjectVersionId, ensureQuery, generateMediaFileId, mediaManager]);

    const prefetch = useCallback(async () => {
        await prefetchData();
        await prefetchFiles();
    }, [prefetchData, prefetchFiles]);

    return {
        prefetchData,
        prefetchFiles,
        prefetch,
    };
}
